import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spin, Tabs } from 'antd';
import { ProfileOutlined } from "@ant-design/icons";
import { axiosErrorHandler } from '../../../../../swzutils';
import { isUserMultiTenant, userHasPermission } from '../../../../../swzutils/UserPermissionsUtil';
import TabDetailsAndContact from "./TabDetailsAndContact";
import TabSettingsAndOptions from "./TabSettingsAndOptions";
import TabLogoDocumentHeader from "./TabLogoDocumentHeader";
import TabApiKeys from "./TabApiKeys";
import TabSystemAdvisories from "./TabSystemAdvisories";
import TabContractorDiscounts from "./TabContractorDiscounts";
import TabInsurancePolicies from "./TabInsurancePolicies";
import TabTimeSlots from "./TabTimeSlots";
import TabIntegrations from "./TabIntegrations";
import TabEmail from "./TabEmail";
import { EditMode } from "../../../enums/EditMode";
import { useNavigate, useSearchParams } from "react-router-dom";
import TenantResource from "../api/TenantResource";
import { loggedInUserSelector } from "../../../../../store/slice/auth";
import { useAppSelector } from "../../../../../store";
export var TenantEditTabKeyEnum;
(function (TenantEditTabKeyEnum) {
    TenantEditTabKeyEnum["DETAILS"] = "DETAILS";
    TenantEditTabKeyEnum["SETTINGS"] = "SETTINGS";
    TenantEditTabKeyEnum["LOGO"] = "LOGO";
    TenantEditTabKeyEnum["API_KEYS"] = "API_KEYS";
    TenantEditTabKeyEnum["EMAIL"] = "EMAIL";
    TenantEditTabKeyEnum["ADVISORIES"] = "ADVISORIES";
    TenantEditTabKeyEnum["INSURANCE_POLICIES"] = "INSURANCE_POLICIES";
    TenantEditTabKeyEnum["CONTRACTOR_DISCOUNTS"] = "CONTRACTOR_DISCOUNTS";
    TenantEditTabKeyEnum["TIME_SLOTS"] = "TIME_SLOTS";
    TenantEditTabKeyEnum["INTEGRATIONS"] = "INTEGRATIONS";
})(TenantEditTabKeyEnum || (TenantEditTabKeyEnum = {}));
export const TENANT_EDIT_ACTIVE_TAB_KEY_PARAM_NAME = "activeTabKey";
const TenantEditForm = ({ tenantId, mode }) => {
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [networkRequestInProgress, setNetworkRequestInProgress] = useState(false);
    const [tenant, setTenant] = useState(undefined);
    const [activeTabKey, setActiveTabKey] = useState(searchParams.get(TENANT_EDIT_ACTIVE_TAB_KEY_PARAM_NAME) || TenantEditTabKeyEnum.DETAILS);
    useEffect(() => {
        if (tenantId) {
            loadTenant();
        }
    }, [tenantId]);
    const loadTenant = () => {
        if (tenantId) {
            setNetworkRequestInProgress(true);
            TenantResource.get(tenantId, setTenant, axiosErrorHandler, () => setNetworkRequestInProgress(false));
        }
    };
    const onTabKeyChanged = (tabKey) => {
        setActiveTabKey(tabKey);
        setSearchParams(params => {
            params.set(TENANT_EDIT_ACTIVE_TAB_KEY_PARAM_NAME, tabKey);
            return params;
        });
    };
    const onTenantUpdated = (tenant) => {
        if (mode == EditMode.EDIT) {
            setTenant(tenant);
        }
        else {
            navigate(`/app/tenants/edit/${tenant.id}`);
        }
    };
    const title = mode === EditMode.CREATE ? "Create a new Profile" : (tenant ? tenant.name : " ");
    const tabPlacement = window.innerWidth < 800 ? "top" : "start";
    const buildTabItems = (tenantId) => {
        let result = [];
        result.push({
            key: TenantEditTabKeyEnum.DETAILS,
            label: 'Company Details and Contacts',
            children: _jsx(TabDetailsAndContact, { tenant: tenant, onTenantUpdated: onTenantUpdated, onConcurrentModificationError: () => loadTenant() })
        });
        if (userHasPermission(loggedInUser, [
            'admin:tenant:settings:updateEmailPOD', // The settings here align to the settings in this tab.
            'admin:tenant:settings:loadChecklist', // This is so the tab is hidden if the user has none of
            'admin:tenant:settings:deliveryChecklist', // of the listed settings
            'admin:consignmentFreight:split',
            'admin:consignmentFreight:separate',
            'admin:driverTimesheet:list',
            'admin:tenant:settings:email',
            'admin:tenant:settings:driverStartNotification',
            'admin:tenant:settings:deliveryNotifications'
        ])) {
            result.push({
                key: TenantEditTabKeyEnum.SETTINGS,
                label: 'Settings and Options',
                children: _jsx(TabSettingsAndOptions, { tenantId: tenantId })
            });
        }
        if (userHasPermission(loggedInUser, ['admin:documentHeader:view'])) {
            result.push({
                key: TenantEditTabKeyEnum.LOGO,
                label: 'Logo and Document Header',
                children: _jsx(TabLogoDocumentHeader, { tenantId: tenantId })
            });
        }
        if (userHasPermission(loggedInUser, ['admin:apiKey:list'])) {
            result.push({
                key: TenantEditTabKeyEnum.API_KEYS,
                label: 'API Keys',
                children: _jsx(TabApiKeys, { tenantId: tenantId })
            });
        }
        if (userHasPermission(loggedInUser, ['admin:tenant:settings:email'])) {
            result.push({
                key: TenantEditTabKeyEnum.EMAIL,
                label: 'Email',
                children: _jsx(TabEmail, { tenantId: tenantId })
            });
        }
        if (userHasPermission(loggedInUser, ['admin:tenant:advisories:list'])) {
            result.push({
                key: TenantEditTabKeyEnum.ADVISORIES,
                label: 'System Email Advisories',
                children: _jsx(TabSystemAdvisories, { tenantId: tenantId })
            });
        }
        if (userHasPermission(loggedInUser, ['admin:tenant:insurancePolicy:list'])) {
            result.push({
                key: TenantEditTabKeyEnum.INSURANCE_POLICIES,
                label: 'Insurance Policies',
                children: _jsx(TabInsurancePolicies, { tenantId: tenantId })
            });
        }
        if (userHasPermission(loggedInUser, ['admin:tenant:contractorDiscounts:list'])) {
            result.push({
                key: TenantEditTabKeyEnum.CONTRACTOR_DISCOUNTS,
                label: 'Contractor Discounts',
                children: _jsx(TabContractorDiscounts, { tenantId: tenantId })
            });
        }
        if (userHasPermission(loggedInUser, ['admin:tenant:timeSlot:list'])) {
            result.push({
                key: TenantEditTabKeyEnum.TIME_SLOTS,
                label: 'Time Slots',
                children: _jsx(TabTimeSlots, { tenantId: tenantId })
            });
        }
        if (userHasPermission(loggedInUser, ['admin:tenant:settings:integrations'])) {
            result.push({
                key: TenantEditTabKeyEnum.INTEGRATIONS,
                label: 'Integrations',
                children: _jsx(TabIntegrations, { tenantId: tenantId })
            });
        }
        return result;
    };
    const tabItems = useMemo(() => buildTabItems(tenantId), [loggedInUser, tenantId, tenant]);
    return (_jsxs("article", { className: "article swz-fixed-article", children: [_jsx("h2", { className: "article-title", children: "Settings and Configuration" }), _jsxs("div", { className: "table-operations", children: [userHasPermission(loggedInUser, ['admin:documentHeader:view']) && isUserMultiTenant(loggedInUser) &&
                        _jsx(Button, { className: "pull-right", href: "#/app/tenants/list", icon: _jsx(ProfileOutlined, {}), children: "Profiles" }), _jsx("h2", { className: "title", children: title })] }), _jsx("div", { className: "swz-fixed-content", children: _jsx("div", { className: "full-height", children: _jsx(Spin, { spinning: networkRequestInProgress, children: mode === EditMode.CREATE ?
                            _jsx(TabDetailsAndContact, { onTenantUpdated: onTenantUpdated })
                            :
                                _jsx(Tabs, { activeKey: activeTabKey, onChange: onTabKeyChanged, tabPlacement: tabPlacement, items: tabItems }) }) }) })] }));
};
export default TenantEditForm;
